import { subDays } from 'date-fns';
import { normalizeOrderDayKey, toDateFromServer, toDayString } from '../api/dates';
import { BASKET_STALE_ATTENTION_BUFFER_DAYS } from '../api/constants';
import type { ModelComplexity } from '../types';
import { getGeneralOrderDeadlineDay, getAthensTodayDayString } from './orderDeadlines';
import type { CheckoutDayBlockCategory } from './orderDeadlines';

export type StaleBasketEntryInput = {
    date: string;
    kind: 'new' | 'change';
    message: string;
    category: CheckoutDayBlockCategory;
};

export type StaleBasketGroup = {
    category: CheckoutDayBlockCategory;
    items: StaleBasketEntryInput[];
};

const STALE_GROUP_ORDER: CheckoutDayBlockCategory[] = ['general_deadline', 'set_menu_deadline', 'no_orderable_meals'];

/**
 * Oldest order day still shown in stale-attention UI: `orderDays` before the earliest open general-order day,
 * using the same deadline clock as daily meals / changes / cancellations (`orderTime`, weekend rules).
 */
export function getBasketStaleAttentionFloorDay(
    modelConstants: ModelComplexity | null | undefined,
    at: Date,
    bufferDays: number = BASKET_STALE_ATTENTION_BUFFER_DAYS,
): string {
    const orderDays = modelConstants?.orderDays ?? 1;
    const deadlineDay = getGeneralOrderDeadlineDay(modelConstants, at);
    return toDayString(subDays(toDateFromServer(deadlineDay), orderDays + bufferDays));
}

/** Stale-attention UI lists blocked days with order day on/after the floor (no RTDB delete). */
export function isOrderDayWithinBasketStaleAttentionWindow(
    orderDay: string,
    modelConstants: ModelComplexity | null | undefined,
    at: Date,
    bufferDays: number = BASKET_STALE_ATTENTION_BUFFER_DAYS,
): boolean {
    const day = normalizeOrderDayKey(orderDay);
    // Upcoming (and today’s) meal days stay visible after cutoff — the deadline-day floor alone
    // can leap over them when orderTime passes (Fri order for Mon shifts the floor past Fri/Sat).
    if (day >= getAthensTodayDayString(at)) {
        return true;
    }
    return day >= getBasketStaleAttentionFloorDay(modelConstants, at, bufferDays);
}

/** Group stale basket rows for accordion sections (stable category order). */
export function groupStaleBasketEntriesByCategory(items: StaleBasketEntryInput[]): StaleBasketGroup[] {
    const byCategory = new Map<CheckoutDayBlockCategory, StaleBasketEntryInput[]>();
    for (const item of items) {
        const list = byCategory.get(item.category) ?? [];
        list.push(item);
        byCategory.set(item.category, list);
    }
    return STALE_GROUP_ORDER
        .filter(category => (byCategory.get(category)?.length ?? 0) > 0)
        .map(category => ({
            category,
            items: [...(byCategory.get(category) ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
        }));
}
